import { Component, computed, input, linkedSignal } from '@angular/core';
import type { ChartEventPeriod, Graph, Series } from '../../../../../models/graph';
import { ReusableChartComponent } from '../../../../../shared-components/charts/reusable-chart/reusable-chart.component';
import { UC2_PRESSURE_ROLE_LABELS } from '../../models/uc2-view-state';
import { UC2_TRACE_STYLES } from '../../mappers/uc2-pressure-events.mapper';

@Component({
  selector: 'app-uc2-pressure-trends',
  imports: [ReusableChartComponent],
  templateUrl: './uc2-pressure-trends.component.html',
  styleUrl: './uc2-pressure-trends.component.scss',
})
export class Uc2PressureTrendsComponent {
  graphs = input.required<Graph[]>();
  period = input<ChartEventPeriod | null>(null);
  firstObservedAt = input<string | null>(null);
  hidden = linkedSignal<Graph[], ReadonlySet<string>>({ source: this.graphs, computation: () => new Set() });
  traces = computed(() => this.graphs().flatMap(graph => graph.series));
  wells = computed(() => [...new Set(this.traces().map(trace => trace.wellCode!))]);
  types = computed(() => {
    const available = new Set(this.traces().map(trace => trace.measurement!));
    return [...new Set([...Object.keys(UC2_TRACE_STYLES), ...available])].filter(type => available.has(type));
  });
  shownCount = computed(() => this.traces().filter(trace => !this.hidden().has(trace.id)).length);

  label(type: string): string {
    return UC2_PRESSURE_ROLE_LABELS[type as keyof typeof UC2_PRESSURE_ROLE_LABELS] ?? type.replaceAll('-', ' ');
  }

  matching(well?: string, type?: string): Series[] {
    return this.traces().filter(trace => (!well || trace.wellCode === well) && (!type || trace.measurement === type));
  }

  checked(well?: string, type?: string): boolean {
    const traces = this.matching(well, type);
    return traces.length > 0 && traces.every(trace => !this.hidden().has(trace.id));
  }

  mixed(well?: string, type?: string): boolean {
    const traces = this.matching(well, type);
    const shown = traces.filter(trace => !this.hidden().has(trace.id)).length;
    return shown > 0 && shown < traces.length;
  }

  toggle(checked: boolean, well?: string, type?: string): void {
    this.hidden.update(previous => {
      const next = new Set(previous);
      for (const trace of this.matching(well, type)) {
        if (checked) next.delete(trace.id);
        else next.add(trace.id);
      }
      return next;
    });
  }
}
